/**
 * PublishDialog — Radix Dialog that lets a Vault GI owner publish
 * their GI to the marketplace. Opened from a Vault card.
 */

import * as Dialog from '@radix-ui/react-dialog';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import type { CertificationLevel, GiSummary } from '@/api/types';
import { usePublishToMarketplace } from '@/hooks/useMarketplace';

const CERTS: CertificationLevel[] = ['none', 'bronze', 'silver', 'gold'];

export function PublishDialog({
  gi,
  open,
  onOpenChange,
}: {
  gi: GiSummary;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { t } = useTranslation(['marketplace', 'common']);
  const publish = usePublishToMarketplace();

  const [tagline, setTagline] = useState('');
  const [description, setDescription] = useState(gi.description);
  const [price, setPrice] = useState('0');
  const [certification, setCertification] = useState<CertificationLevel>('none');

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (publish.isPending) return;
    await publish.mutateAsync({
      root_id: gi.root_id,
      tagline: tagline.trim() || gi.description,
      description: description.trim() || gi.description,
      price_usd: Math.max(0, parseInt(price, 10) || 0),
      certification,
    });
    onOpenChange(false);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in" />
        {/* Viewport-relative centering: `left-1/2` + `-translate-x-1/2`
            is geometry in screen space, not text flow. The dialog
            renders in the same physical center under LTR and RTL
            (viewport has no bidi origin), so logical `start-*` is
            NOT what we want here — it would offset the centre to the
            trailing edge under RTL. */}
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(92vw,520px)] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-surface p-6 shadow-float focus:outline-none">
          <Dialog.Title className="text-lg font-semibold text-ink">
            {t('publish.title')}
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-ink-muted">
            {t('publish.subtitle', { name: gi.name })}
          </Dialog.Description>

          <form onSubmit={onSubmit} className="mt-5 flex flex-col gap-4">
            <Field
              label={t('publish.tagline_label')}
              hint={t('publish.tagline_hint')}
            >
              <input
                type="text"
                value={tagline}
                onChange={(e) => setTagline(e.target.value)}
                maxLength={140}
                placeholder={gi.description}
                className="rounded-md border border-border bg-surface px-3 py-2 text-sm text-ink focus-visible:border-spark"
              />
            </Field>

            <Field
              label={t('publish.description_label')}
              hint={t('publish.description_hint')}
            >
              <textarea
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="rounded-md border border-border bg-surface px-3 py-2 text-sm text-ink focus-visible:border-spark"
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label={t('publish.price_label')} hint={t('publish.price_hint')}>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  className="rounded-md border border-border bg-surface px-3 py-2 text-sm text-ink focus-visible:border-spark"
                />
              </Field>

              <Field label={t('publish.cert_label')} hint={t('publish.cert_hint')}>
                <select
                  value={certification}
                  onChange={(e) => setCertification(e.target.value as CertificationLevel)}
                  className="rounded-md border border-border bg-surface px-3 py-2 text-sm text-ink focus-visible:border-spark"
                >
                  {CERTS.map((c) => (
                    <option key={c} value={c}>
                      {t(`filters.cert.${c}`)}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            {publish.isError ? (
              <p role="alert" className="text-sm text-danger">
                {t('publish.error')}
              </p>
            ) : null}

            <div className="mt-2 flex items-center justify-end gap-2 border-t border-border pt-4">
              <Dialog.Close asChild>
                <Button type="button" variant="ghost">
                  {t('actions.cancel', { ns: 'common' })}
                </Button>
              </Dialog.Close>
              <Button
                type="submit"
                variant="primary"
                disabled={publish.isPending}
              >
                {publish.isPending ? t('publish.submitting') : t('publish.submit')}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-sm font-medium text-ink">{label}</span>
      {children}
      {hint ? <span className="text-xs text-ink-muted">{hint}</span> : null}
    </label>
  );
}
